import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, 
  GraduationCap,
  Clock,
  Target,
  Loader2,
  Sparkles,
  CheckCircle2,
  Circle,
  Trash2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { GlassCard } from '../components/layout/GlassCard';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LearningHubPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [paths, setPaths] = useState([]);
  const [expandedPath, setExpandedPath] = useState(null);
  
  // New path form
  const [pathForm, setPathForm] = useState({
    skill: '',
    current_level: 'beginner',
    goal: '',
    time_commitment: '1-2 hours/day'
  });

  const loadPaths = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/growth/learning/paths`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setPaths(response.data.paths || []);
    } catch (error) {
      console.error('Failed to load paths');
    }
  }, [token]);

  useEffect(() => {
    loadPaths();
  }, [loadPaths]);

  const createPath = async () => {
    if (!pathForm.skill.trim()) {
      toast.error('Please enter a skill to learn');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/growth/learning/path`, pathForm, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Learning path created!');
      loadPaths();
      setPathForm({ skill: '', current_level: 'beginner', goal: '', time_commitment: '1-2 hours/day' });
    } catch (error) {
      toast.error('Failed to create path');
    } finally {
      setLoading(false);
    }
  };

  const updateProgress = async (pathId, newProgress) => {
    try {
      await axios.patch(`${API_URL}/api/growth/learning/path/${pathId}/progress`, 
        { progress: newProgress },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      loadPaths();
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const deletePath = async (pathId) => {
    try {
      await axios.delete(`${API_URL}/api/growth/learning/path/${pathId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Path deleted');
      loadPaths();
    } catch (error) {
      toast.error('Failed to delete path');
    }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="learning-hub-page">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">AIRA Learning Hub</h1>
        <p className="text-gray-400">AI-powered personalized learning paths</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Create Path Form */}
        <GlassCard className="p-6 lg:col-span-1">
          <h3 className="font-heading text-xl font-semibold mb-6 flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-primary" />
            New Learning Path
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Skill to Learn</label>
              <input
                type="text"
                value={pathForm.skill}
                onChange={(e) => setPathForm(prev => ({ ...prev, skill: e.target.value }))}
                className="input-field w-full"
                placeholder="e.g., Python, Machine Learning, UI Design"
                data-testid="skill-input"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Current Level</label>
              <select
                value={pathForm.current_level}
                onChange={(e) => setPathForm(prev => ({ ...prev, current_level: e.target.value }))}
                className="input-field w-full"
                data-testid="level-select"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Learning Goal</label>
              <textarea
                value={pathForm.goal}
                onChange={(e) => setPathForm(prev => ({ ...prev, goal: e.target.value }))}
                className="input-field w-full h-24 resize-none"
                placeholder="What do you want to achieve?"
                data-testid="goal-input"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Time Commitment</label>
              <select
                value={pathForm.time_commitment}
                onChange={(e) => setPathForm(prev => ({ ...prev, time_commitment: e.target.value }))}
                className="input-field w-full"
                data-testid="time-select"
              >
                <option value="30 mins/day">30 mins/day</option>
                <option value="1-2 hours/day">1-2 hours/day</option>
                <option value="3+ hours/day">3+ hours/day</option>
                <option value="weekends only">Weekends only</option>
              </select>
            </div>

            <button
              onClick={createPath}
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="create-path-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
              Generate Path
            </button>
          </div>
        </GlassCard>

        {/* Learning Paths List */}
        <div className="lg:col-span-2 space-y-4">
          {paths.length === 0 ? (
            <GlassCard className="p-12 text-center">
              <BookOpen className="w-16 h-16 text-primary/30 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">No Learning Paths</h3>
              <p className="text-gray-400">Create your first AI-generated learning path</p>
            </GlassCard>
          ) : (
            paths.map((path) => (
              <motion.div
                key={path.path_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <GlassCard className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-semibold">{path.skill}</h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <GraduationCap className="w-4 h-4" />
                          {path.current_level}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {path.content?.estimated_duration || 'TBD'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setExpandedPath(expandedPath === path.path_id ? null : path.path_id)}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        {expandedPath === path.path_id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>
                      <button
                        onClick={() => deletePath(path.path_id)}
                        className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-400">Progress</span>
                      <span className="text-primary">{path.progress || 0}%</span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${path.progress || 0}%` }}
                        className="h-full bg-gradient-to-r from-primary to-accent"
                      />
                    </div>
                    <div className="flex gap-2 mt-2">
                      {[25, 50, 75, 100].map((p) => (
                        <button
                          key={p}
                          onClick={() => updateProgress(path.path_id, p)}
                          className={`px-3 py-1 text-xs rounded-full transition-colors ${
                            (path.progress || 0) >= p 
                              ? 'bg-primary/20 text-primary' 
                              : 'bg-white/5 text-gray-500 hover:bg-white/10'
                          }`}
                        >
                          {p}%
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  <AnimatePresence>
                    {expandedPath === path.path_id && path.content && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="border-t border-white/10 pt-4 mt-4"
                      >
                        {/* Modules */}
                        {path.content.modules && (
                          <div className="space-y-3">
                            <h4 className="text-sm text-gray-500 mb-2">Learning Modules</h4>
                            {path.content.modules.map((module, idx) => (
                              <div key={idx} className="p-4 bg-white/5 rounded-xl">
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm text-primary flex-shrink-0">
                                    {idx + 1}
                                  </div>
                                  <div className="flex-1">
                                    <h5 className="font-medium">{module.title}</h5>
                                    <p className="text-sm text-gray-400 mt-1">{module.description}</p>
                                    {module.duration && (
                                      <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                                        <Clock className="w-3 h-3" /> {module.duration}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Milestones */}
                        {path.content.milestones && (
                          <div className="mt-4">
                            <h4 className="text-sm text-gray-500 mb-2">Key Milestones</h4>
                            <div className="flex flex-wrap gap-2">
                              {path.content.milestones.map((m, idx) => (
                                <span key={idx} className="px-3 py-1 bg-accent/20 text-accent rounded-full text-sm flex items-center gap-2">
                                  <Target className="w-3 h-3" />
                                  {m}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Career Applications */}
                        {path.content.career_applications && (
                          <div className="mt-4 p-4 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl">
                            <h4 className="text-sm text-gray-400 mb-1">Career Applications</h4>
                            <p className="text-sm">{path.content.career_applications}</p>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </GlassCard>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
